import type { FriendInfo, PlayerId } from '@pixelworld/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/Button';
import { findClientGame } from '../../game-host/registry';
import { da } from '../../i18n/da';
import { answerFriendRequest, dismissInvite, removeFriend } from './friends-api';
import { friendAction, friendOrder, friendUrl } from './friend-where';
import { useFriends } from './useFriends';
import styles from './FriendsPanel.module.css';

const REFRESH_MS = 10_000;

/**
 * The friends list on the start page (ADR 0019): who plays where, requests and "Kom og spil".
 * Friends are made in a room, under "Spillere".
 */
export function FriendsPanel() {
  const navigate = useNavigate();
  const { overview, reload } = useFriends(REFRESH_MS);
  const [removing, setRemoving] = useState<PlayerId | null>(null);

  if (!overview) return null;
  const friends = [...overview.friends].sort((a, b) => friendOrder(a.where) - friendOrder(b.where));
  const nothing =
    friends.length === 0 && overview.incoming.length === 0 && overview.invites.length === 0;

  const answer = async (playerId: PlayerId, accept: boolean) => {
    await answerFriendRequest(playerId, accept);
    reload();
  };
  const remove = async (playerId: PlayerId) => {
    setRemoving(null);
    await removeFriend(playerId);
    reload();
  };
  const dismiss = async (playerId: PlayerId) => {
    await dismissInvite(playerId);
    reload();
  };

  return (
    <section className={styles.panel} aria-labelledby="friends-title" data-testid="friends">
      <h3 id="friends-title" className={styles.title}>
        {da.friends.title}
      </h3>

      {overview.invites.map((invite) => (
        <div key={invite.from.id} className={styles.notice} role="group">
          <span>{da.friends.inviteFrom(invite.from.displayName)}</span>
          <span className={styles.buttons}>
            <Button
              size="small"
              onClick={() => void navigate(friendUrl(invite.gameId, invite.from.id))}
            >
              {da.friends.goWith}
            </Button>
            <Button size="small" variant="quiet" onClick={() => void dismiss(invite.from.id)}>
              {da.friends.noThanks}
            </Button>
          </span>
        </div>
      ))}

      {overview.incoming.map((request) => (
        <div key={request.id} className={styles.notice} role="group">
          <span>{da.friends.requestFrom(request.displayName)}</span>
          <span className={styles.buttons}>
            <Button size="small" onClick={() => void answer(request.id, true)}>
              {da.friends.yes}
            </Button>
            <Button size="small" variant="quiet" onClick={() => void answer(request.id, false)}>
              {da.friends.noThanks}
            </Button>
          </span>
        </div>
      ))}

      {nothing && <p className={styles.empty}>{da.friends.empty}</p>}

      {friends.length > 0 && (
        <ul className={styles.friends}>
          {friends.map((friend) =>
            removing === friend.id ? (
              <li key={friend.id} className={styles.friend}>
                <span className={styles.who}>{da.friends.removeAsk(friend.displayName)}</span>
                <span className={styles.buttons}>
                  <Button size="small" variant="secondary" onClick={() => void remove(friend.id)}>
                    {da.friends.removeYes}
                  </Button>
                  <Button size="small" variant="quiet" onClick={() => setRemoving(null)}>
                    {da.friends.cancel}
                  </Button>
                </span>
              </li>
            ) : (
              <FriendRow
                key={friend.id}
                friend={friend}
                onGo={(gameId) => void navigate(friendUrl(gameId, friend.id))}
                onRemove={() => setRemoving(friend.id)}
              />
            ),
          )}
        </ul>
      )}
    </section>
  );
}

function FriendRow({
  friend,
  onGo,
  onRemove,
}: {
  friend: FriendInfo;
  onGo: (gameId: string) => void;
  onRemove: () => void;
}) {
  const { where } = friend;
  const action = friendAction(where);
  const place =
    where.status === 'playing'
      ? da.friends.playing(
          findClientGame(where.gameId)?.title ?? where.gameId,
          where.visibility === 'private',
        )
      : where.status === 'start'
        ? da.friends.onStart
        : da.friends.offline;
  return (
    <li className={styles.friend} data-testid="friend">
      <span
        className={where.status === 'offline' ? styles.dot : `${styles.dot} ${styles.online}`}
        aria-hidden="true"
      />
      <span className={styles.who}>
        <strong>{friend.displayName}</strong>
        <small>{place}</small>
      </span>
      <span className={styles.buttons}>
        {where.status === 'playing' && action && (
          <Button size="small" variant="secondary" onClick={() => onGo(where.gameId)}>
            {action === 'goTo' ? da.friends.goTo(friend.displayName) : da.friends.knock}
          </Button>
        )}
        <button type="button" className={styles.remove} onClick={onRemove}>
          {da.friends.remove}
        </button>
      </span>
    </li>
  );
}
